import { useState, useEffect } from "react";
import { WaitlistButton } from "@/components/landing/WaitlistButton";
import { LOGO_SRC } from "@/config";
import { Menu, X } from "lucide-react";

const NAV_LINKS = [
  { label: "How It Works", href: "#how-it-works" },
  { label: "Benefits", href: "#benefits" },
  { label: "Explore App", href: "#inside-the-app" },
  { label: "About", href: "#about" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 backdrop-blur-md bg-[#F9F6F0]/90 border-b transition-colors duration-300 ${
        scrolled ? "border-[#4A6B41]/15 shadow-sm" : "border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12 py-2 flex items-center justify-between">
        <a
          data-testid="navbar-logo-link"
          href="#top"
          className="flex items-center gap-3"
        >
          <img
            src={LOGO_SRC}
            alt="Source2Table logo"
            className="h-16 w-16 sm:h-20 sm:w-20 object-contain"
          />
          <span className="font-sans font-extrabold text-2xl sm:text-4xl leading-none tracking-tight">
            <span className="text-black">Source</span>
            <span className="text-[#4A6B41]">2</span>
            <span className="text-black">Table</span>
          </span>
        </a>

        <nav className="hidden xl:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              data-testid={`navbar-link-${l.href.replace("#", "")}`}
              href={l.href}
              className="font-sans text-sm font-medium text-[#5A6757] hover:text-[#4A6B41] transition-colors"
            >
              {l.label}
            </a>
          ))}
          <a
            data-testid="navbar-producer-signin-btn"
            href="https://producers.source2table.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-full font-sans font-semibold tracking-wide bg-[#C2b6a8] text-[#2F4829] px-7 py-3 text-sm sm:text-base shadow-lg shadow-[#C2b6a8]/30 hover:bg-[#b3a595] transition-transform duration-200 hover:scale-105 active:scale-95 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#C2b6a8]/40"
          >
            Producer Sign In
          </a>
          <WaitlistButton testid="navbar-join-waitlist-btn" variant="solid" />
        </nav>

        <button
          data-testid="navbar-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="xl:hidden p-2 rounded-lg text-[#2F4829] hover:bg-[#4A6B41]/10"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="navbar-mobile-menu"
          className="xl:hidden border-t border-[#4A6B41]/15 bg-[#F9F6F0] px-6 py-5 flex flex-col gap-4"
        >
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              data-testid={`navbar-mobile-link-${l.href.replace("#", "")}`}
              href={l.href}
              onClick={() => setOpen(false)}
              className="font-sans text-base font-medium text-[#1C241B]"
            >
              {l.label}
            </a>
          ))}
          <a
            data-testid="navbar-mobile-producer-signin-btn"
            href="https://producers.source2table.com"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="w-full inline-flex items-center justify-center rounded-full border border-[#4A6B41]/40 px-5 py-3 font-sans text-sm font-semibold text-[#2F4829] hover:bg-[#4A6B41]/10 transition-colors"
          >
            Producer Sign In
          </a>
          <WaitlistButton
            testid="navbar-mobile-join-waitlist-btn"
            variant="solid"
            className="w-full"
          />
        </div>
      )}
    </header>
  );
};
